import Model, { hasMany, belongsTo, attr } from '@ember-data/model'
import { Type } from '@warp-drive/core-types/symbols'
import type ContactDetail from 'district-ui-client/models/contact-detail'
import type SchoolClass from 'district-ui-client/models/school-class'
import type SchoolSubscription from 'district-ui-client/models/school-subscription'
import type Teacher from 'district-ui-client/models/teacher'
import type SubscriptionCoordinator from 'district-ui-client/models/subscription-coordinator'
import type Student from 'district-ui-client/models/student'
import type SchoolSubscriptionUtilisation from 'district-ui-client/models/school-subscription-utilisation'
import type { SubscriptionType } from 'district-ui-client/domain/subscription-type'
import type SchoolRollover from 'district-ui-client/models/school-rollover'
import type StringTransform from 'district-ui-client/transforms/string'
import type BooleanTransform from 'district-ui-client/transforms/boolean'

export default class School extends Model {
  @attr<StringTransform>('string') declare name: string

  @attr<StringTransform>('string') declare schoolCode: string

  @attr<StringTransform>('string') declare externalRosteringSource: string

  @attr<BooleanTransform>('boolean') declare usesExternalRostering: boolean

  /**
   * AU, GB, US, ... ISO_3166-1 alpha-2 codes https://en.wikipedia.org/wiki/ISO_3166-1
   */
  @attr<StringTransform>('string') declare countryCode: string

  @attr<StringTransform>('string') declare oneRosterEnabledAt?: Nullable<string>

  @belongsTo<ContactDetail>('contact-detail', { async: false, inverse: 'school' }) declare contactDetail?: ContactDetail

  @hasMany<SchoolSubscription>('school-subscription', { async: false, inverse: 'school' })
  declare schoolSubscriptions?: SchoolSubscription[]

  @hasMany<SchoolSubscriptionUtilisation>('school-subscription-utilisation', { async: false, inverse: 'school' })
  declare schoolSubscriptionUtilisations?: SchoolSubscriptionUtilisation[]

  @hasMany<SchoolClass>('school-class', { async: false, inverse: 'school' }) declare schoolClasses?: SchoolClass[]

  @hasMany<Teacher>('teacher', { async: false, inverse: 'school' }) declare teachers?: Teacher[]

  @hasMany<Student>('student', { async: false, inverse: 'school' }) declare students?: Student[]

  @hasMany<SubscriptionCoordinator>('subscription-coordinator', { async: false, inverse: 'school' })
  declare subscriptionCoordinators?: SubscriptionCoordinator[]

  @hasMany<SchoolRollover>('school-rollover', { async: false, inverse: 'school' })
  declare schoolRollovers?: SchoolRollover[]

  teachersForSubscriptionType(subscriptionType: SubscriptionType): Teacher[] {
    return this.teachers?.filter((t) => t.schoolClasses?.some((sc) => sc.subscriptionType === subscriptionType)) ?? []
  }

  utilisationFor(subscriptionType: SubscriptionType) {
    return this.schoolSubscriptionUtilisations?.find((utilisation) => utilisation.subscriptionType === subscriptionType)
  }

  // since utilisations are slow to request (due to the counts), this can be a cheaper way to determine active schools
  hasActiveSchoolSubscriptionFor(subscriptionType: SubscriptionType): boolean {
    return Boolean(this.schoolSubscriptions?.some((sub) => sub.subscriptionType === subscriptionType && sub.isActive))
  }

  [Type] = 'school' as const
}
